import type { IJsonObject } from '../../../globalTypes';
import { eventEmitter } from '../../../lib/eventEmmitter';
import type { IStorageResponse } from '../types';
import { sendMessageToNative } from './sendMessageToNative';

interface IStoragePayload extends IJsonObject {
  requestId: string;
  storageAction: 'getItem' | 'setItem' | 'removeItem' | 'clear';
  key?: string;
  value?: string;
}

const genRequestId = (): string => `storage_${Date.now()}_${Math.random().toString(36).slice(2)}`;

const isNative = (): boolean => typeof window !== 'undefined' && !!window.ReactNativeWebView;

const awaitResponse = (requestId: string): Promise<IStorageResponse> =>
  new Promise((resolve) => {
    const off = eventEmitter.on('STORAGE_RESPONSE', (resp) => {
      if ((resp as IStorageResponse).requestId === requestId) {
        off();
        resolve(resp as IStorageResponse);
      }
    });
  });

export const nativeGetItem = async (key: string): Promise<string | null> => {
  if (!isNative()) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }
  const requestId = genRequestId();
  const payload: IStoragePayload = { requestId, storageAction: 'getItem', key };
  sendMessageToNative('STORAGE', payload);
  const { result } = await awaitResponse(requestId);
  return result.success ? ((result.result as string | undefined) ?? null) : null;
};

export const nativeSetItem = async (key: string, value: string): Promise<boolean> => {
  if (!isNative()) {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch {
      return false;
    }
  }
  const requestId = genRequestId();
  const payload: IStoragePayload = { requestId, storageAction: 'setItem', key, value };
  sendMessageToNative('STORAGE', payload);
  const { result } = await awaitResponse(requestId);
  return result.success;
};

export const nativeRemoveItem = async (key: string): Promise<boolean> => {
  if (!isNative()) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch {
      return false;
    }
  }
  const requestId = genRequestId();
  const payload: IStoragePayload = { requestId, storageAction: 'removeItem', key };
  sendMessageToNative('STORAGE', payload);
  const { result } = await awaitResponse(requestId);
  return result.success;
};

export const nativeClear = async (): Promise<boolean> => {
  if (!isNative()) {
    try {
      localStorage.clear();
      return true;
    } catch {
      return false;
    }
  }
  const requestId = genRequestId();
  const payload: IStoragePayload = { requestId, storageAction: 'clear' };
  sendMessageToNative('STORAGE', payload);
  const { result } = await awaitResponse(requestId);
  return result.success;
};
